import { useState } from 'react';

import { magicLinkRequest } from './magicLinkRequest.js';
import { signInDefaults } from './signInDefaults.js';
import { supabase } from './supabase.js';

const defaults = signInDefaults(import.meta.env.DEV);

/**
 * Passwordless email Magic Link. `shouldCreateUser: false` is a second guard on
 * top of the project-wide disabled sign-ups: an address other than the one
 * provisioned user can never turn this form into registration.
 */
export const SignIn = (): React.JSX.Element => {
  const [email, setEmail] = useState(defaults.email);
  const [linkSent, setLinkSent] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setSubmitting(true);
    setProblem(null);

    try {
      const { error } = await supabase.auth.signInWithOtp(
        magicLinkRequest(email, window.location.origin),
      );
      if (error) throw error;
      setLinkSent(true);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'Sign-in failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={(event) => void submit(event)}>
      <label htmlFor="email">Email</label>
      <input
        id="email"
        name="email"
        type="email"
        autoComplete="email"
        autoFocus
        disabled={linkSent}
        value={email}
        onChange={(event) => {
          setEmail(event.target.value);
        }}
      />

      {!linkSent && (
        <button type="submit" disabled={submitting || email === ''}>
          {submitting ? 'Sending…' : 'Send sign-in link'}
        </button>
      )}
      {linkSent && (
        <p role="status">
          Check your email and open the sign-in link. It expires shortly and works once.
          {defaults.magicLinkInboxUrl !== undefined && (
            <>
              {' '}
              <a href={defaults.magicLinkInboxUrl} target="_blank" rel="noreferrer">
                Open the local Magic Link inbox in a new tab.
              </a>
            </>
          )}
        </p>
      )}
      {linkSent && (
        <button
          type="button"
          disabled={submitting}
          onClick={() => {
            setLinkSent(false);
            setProblem(null);
          }}
        >
          Use a different email
        </button>
      )}
      {problem !== null && <p role="alert">{problem}</p>}
    </form>
  );
};
